import {
  PropsWithChildren,
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactElement,
  cloneElement,
  isValidElement,
  useId,
} from "react";

export function Card({ children, className = "" }: PropsWithChildren<{ className?: string }>) {
  return (
    <div className={`rounded-card bg-surface p-6 shadow-card ${className}`}>{children}</div>
  );
}

export function DarkPanel({ children, className = "" }: PropsWithChildren<{ className?: string }>) {
  return (
    <div className={`rounded-card bg-panel p-6 text-white shadow-card ${className}`}>{children}</div>
  );
}

export function Pill({
  active,
  children,
  onClick,
}: PropsWithChildren<{ active?: boolean; onClick?: () => void }>) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-pill px-4 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${
        active ? "bg-primary text-white" : "text-white/70 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "ghost" | "danger";
}

export function Button({ variant = "primary", className = "", type, ...rest }: ButtonProps) {
  const base =
    "rounded-pill px-5 py-2.5 text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50";
  const style =
    variant === "primary"
      ? "bg-primary text-white hover:bg-primary-deep"
      : variant === "danger"
        ? "bg-danger text-white hover:bg-danger/90"
        : "bg-white text-panel border border-panel/10 hover:bg-canvas";
  return <button type={type ?? "button"} className={`${base} ${style} ${className}`} {...rest} />;
}

export function Label({ children, htmlFor }: PropsWithChildren<{ htmlFor?: string }>) {
  return (
    <label htmlFor={htmlFor} className="block text-xs font-medium text-muted mb-1.5">
      {children}
    </label>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`w-full rounded-card border border-panel/10 bg-white px-4 py-2.5 text-sm text-panel focus:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${props.className ?? ""}`}
    />
  );
}

export function Field({
  label,
  children,
}: PropsWithChildren<{ label: string }>) {
  const id = useId();
  // Wire label→input via useId so screen readers announce the field name
  // instead of "edit text" for every input in the app.
  const child = isValidElement(children)
    ? cloneElement(children as ReactElement<InputHTMLAttributes<HTMLInputElement>>, {
        id: (children as ReactElement<{ id?: string }>).props.id ?? id,
      })
    : children;
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      {child}
    </div>
  );
}

export function Section({
  title,
  hint,
  children,
}: PropsWithChildren<{ title: string; hint?: string }>) {
  return (
    <Card>
      <div className="mb-4">
        <div className="font-medium">{title}</div>
        {hint && <div className="mt-1 text-sm text-muted">{hint}</div>}
      </div>
      {children}
    </Card>
  );
}

const TONES = {
  neutral: "bg-canvas text-muted",
  good: "bg-success/10 text-success",
  bad: "bg-danger/10 text-danger",
  warn: "bg-primary/10 text-primary-deep",
} as const;

export function Badge({
  tone = "neutral",
  children,
}: PropsWithChildren<{ tone?: keyof typeof TONES }>) {
  return (
    <span className={`rounded-pill px-2.5 py-1 text-xs font-medium ${TONES[tone]}`}>
      {children}
    </span>
  );
}

export function Rows({ children }: PropsWithChildren) {
  return <div className="divide-y divide-panel/5">{children}</div>;
}

export function Row({ children }: PropsWithChildren) {
  return <div className="flex items-center justify-between gap-3 py-2.5 text-sm">{children}</div>;
}

export function Empty({ children }: PropsWithChildren) {
  return <div className="py-3 text-sm text-muted">{children}</div>;
}
